import React, { useState, useEffect, useRef } from "react";
import styles from "./Gallery.module.css";
import placeholderImg from "../../../assets/images/about-bg.png"; // reusing as placeholder
import api from "../../../api/axios";
import { Camera, X, Search } from "lucide-react";

const galleryCategories = [
  "All Photos",
  "Events",
  "Festivals",
  "Society",
  "Community",
  "Kids",
  "Sports",
];

const Gallery = () => {
  const [galleryImages, setGalleryImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("All Photos");
  const [dynamicCategories, setDynamicCategories] = useState(galleryCategories);
  const [searchQuery, setSearchQuery] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  const getImageUrl = (path) => {
    if (!path) return placeholderImg;
    if (path.startsWith('http')) return path;
    const normalized = path.replace(/\\/g, '/');
    const baseUrl = import.meta.env.VITE_API_URL.replace(/\/api\/?$/, '');
    if (normalized.startsWith('/uploads')) {
        return `${baseUrl}/api${normalized}`;
    }
    return `${baseUrl}${normalized.startsWith('/') ? '' : '/'}${normalized}`;
  };

  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const { data } = await api.get('/public/gallery');
        setGalleryImages(data);

        const derivedAlbums = Array.from(new Set(data.map(img => img.album).filter(Boolean)));
        const newCats = derivedAlbums.filter(c => !galleryCategories.includes(c));
        
        if (newCats.length > 0) {
          setDynamicCategories([...galleryCategories, ...newCats]);
        } else {
          setDynamicCategories(galleryCategories);
        }
      } catch (error) {
        console.error("Failed to fetch gallery", error);
      } finally {
        setLoading(false);
      }
    };
    fetchGallery();
  }, []);

  const filteredImages = galleryImages.filter((img) => {
    const matchesCategory = activeCategory === "All Photos" || img.album === activeCategory;
    const matchesSearch = img.title.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className={styles.galleryPage}>
      <div className={styles.headerSection}>
        <div className={styles.headerText}>
          <h1 className={styles.pageTitle}>Photo Gallery</h1>
          <p className={styles.leadText}>
            A glimpse into our vibrant and beautiful community life.
          </p>
        </div>
      </div>

      <div className={styles.contentWrapper}>
        {/* Search Bar UI */}
        <div className={styles.searchContainerWrapper}>
          <div className={styles.searchBar}>
            
            {/* Category Dropdown */}
            <div className={styles.categoryDropdownContainer} ref={dropdownRef}>
              <button 
                className={styles.dropdownToggle}
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              >
                <div className={styles.activeCategoryDisplay}>
                  <Camera size={18} />
                  <span>{activeCategory === "All Photos" ? "All Categories" : activeCategory}</span>
                </div>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </button>
              
              {isDropdownOpen && (
                <div className={styles.dropdownMenu}>
                  {dynamicCategories.map((category) => (
                    <div 
                      key={category} 
                      className={`${styles.dropdownItem} ${activeCategory === category ? styles.dropdownItemActive : ''}`}
                      onClick={() => {
                        setActiveCategory(category);
                        setIsDropdownOpen(false);
                      }}
                    >
                      <span className={styles.dropdownItemIcon}><Camera size={16} /></span>
                      <span>{category === "All Photos" ? "All Categories" : category}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Divider */}
            <div className={styles.searchDivider}></div>

            {/* Search Input */}
            <div className={styles.searchInputWrapper}>
              <Search size={20} className={styles.searchIcon} />
              <input 
                type="text" 
                placeholder="Search photos by title..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={styles.searchInput}
              />
            </div>

            {/* Search Button */}
            <button className={styles.searchActionBtn}>
              <Search size={18} /> Search
            </button>
          </div>
        </div>

        {/* Gallery Grid */}
        <div className={styles.galleryGrid}>
          {loading ? (
            <p>Loading gallery...</p>
          ) : filteredImages.map((item) => (
            <div 
              key={item._id} 
              className={styles.galleryCard}
              onClick={() => setSelectedImage(getImageUrl(item.url))}
            >
              <img
                src={getImageUrl(item.url)}
                alt={item.title}
                className={styles.galleryImage}
              />
              <div className={styles.cardOverlay}>
                <span className={styles.imageCategory}>{item.album || 'General'}</span>
                <h3 className={styles.imageTitle}>{item.title}</h3>
              </div>
            </div>
          ))}

          {!loading && filteredImages.length === 0 && (
            <div className={styles.noImages} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 20px', backgroundColor: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: '16px', gridColumn: '1 / -1', minHeight: '200px' }}>
              <Camera size={48} color="#94a3b8" style={{ marginBottom: '16px' }} />
              <h3 style={{ fontSize: '18px', color: '#475569', marginBottom: '8px' }}>No Images Found</h3>
              <p style={{ color: '#64748b', textAlign: 'center', margin: 0, fontSize: '14px' }}>There are no {activeCategory !== 'All Photos' ? activeCategory : ''} images in the gallery at the moment.</p>
            </div>
          )}
        </div>
      </div>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div className={styles.lightbox} onClick={() => setSelectedImage(null)}>
          <button className={styles.closeBtn} onClick={() => setSelectedImage(null)}>
            <X size={24} />
          </button>
          <img src={selectedImage} alt="Fullscreen View" className={styles.lightboxImg} onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
};

export default Gallery;
